import {exportToCSV, exportToPDF} from '../../../../utils/exportUtils';
import React, {useRef, useState} from 'react';
import AppCard from '../../../../components/ui/AppCard';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import {InputText} from 'primereact/inputtext';
import {Toast} from 'primereact/toast';
import apiClient from "../../../../services/api-client";

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import StorageHelper from '../../../../utils/storage-helper';

const AgentReportsTable = () => {
    const [payments, setPayments] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => null);
    const [toDate, setToDate] = useState(() => null);
    const [agentId, setAgentId] = useState('');

    const dt = useRef(null);
    const toast = useRef(null);

    const fetchAgentReport = async () => {
        const effectiveFromDate = fromDate ?? new Date();
        const effectiveToDate = toDate ?? new Date();

        setLoading(true);

        try {
            const formattedFromDate = effectiveFromDate.toISOString().split('T')[0];
            const formattedToDate = effectiveToDate.toISOString().split('T')[0];

            const params = {
                from: formattedFromDate,
                to: formattedToDate
            };

            if (agentId?.trim()) {
                params.agentId = agentId.trim();
            }

            const response = await apiClient.get(`report/agents`,
                {
                    params,
                    headers: {
                        Authorization: 'Bearer ' + StorageHelper.get('token')
                    }
                }
            );

            if (response.data.status === "SUCCESS") {
                setPayments(response.data.responseBody);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Agent reports loaded successfully',
                    life: 3000
                });
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch agent reports',
                    life: 3000
                });
            }
        } catch (error) {
            console.log(error);
            let message = 'Failed to fetch agent reports. Please try again.';

            if (error.response) {
                if (error.response.status === 404) {
                    message = error.response.data?.detail || 'Agent not found.';
                } else if (error.response.status === 400) {
                    message = error.response.data?.detail || 'Invalid request.';
                } else if (error.response.status === 500) {
                    message = 'Server error. Please contact support.';
                }
            } else if (error.request) {
                message = 'No response from server. Check your connection.';
            }

            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({selectionOnly});
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const amountBodyTemplate = (rowData) => {
        return `${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusStyle = (status) => {
        if (!status) return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};

        const upperStatus = status.toUpperCase();

        switch (upperStatus) {
            case 'PENDING':
                return {color: '#FCB700', backgroundColor: '#FFF8E1'};
            case 'SUCCESS':
            case 'SUCCESSFUL':
            case 'COMPLETED':
                return {color: '#00D390', backgroundColor: '#E6FFF7'};
            case 'FAILED':
            case 'DECLINED':
                return {color: '#FF6266', backgroundColor: '#FFECEC'};
            case 'PROCESSING':
                return {color: '#00BAFE', backgroundColor: '#E6F7FF'};
            default:
                if (upperStatus.includes('NMB') || upperStatus.includes('CR')) {
                    return {color: '#00D390', backgroundColor: '#E6FFF7'};
                }
                return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};
        }
    };


    const paymentStatusBodyTemplate = (rowData) => {
        const status = rowData.paymentStatus;
        if (!status) return <span
            className="badge border-0 font-semibold text-xs bg-base-200/50 text-base-content">-</span>;
        const upperStatus = status.toUpperCase();
        let bg = 'bg-base-200/50', text = 'text-base-content';
        if (upperStatus === 'PENDING') {
            bg = 'bg-warning/10';
            text = 'text-warning';
        } else if (upperStatus === 'SUCCESS' || upperStatus === 'COMPLETED' || upperStatus === 'SUCCESSFUL' || upperStatus.includes('NMB') || upperStatus.includes('CR')) {
            bg = 'bg-success/10';
            text = 'text-success';
        } else if (upperStatus === 'FAILED' || upperStatus === 'DECLINED') {
            bg = 'bg-error/10';
            text = 'text-error';
        } else if (upperStatus === 'PROCESSING') {
            bg = 'bg-info/10';
            text = 'text-info';
        }
        return <span className={`badge border-0 font-semibold text-xs ${bg} ${text}`}>{status}</span>;
    };

    const paymentMethodBodyTemplate = (rowData) => {
        const method = rowData.paymentMethod;
        if (!method) return null;
        return <span className="badge badge-outline font-semibold text-xs badge-ghost">{method}</span>;
    };

    const globalFilterElement = (
        <InputText
            placeholder="Search payments..."
            value={globalFilter}
            onChange={onGlobalFilter}
            style={{width: '100%'}}
        />
    );

    const items = [
        {
            label: 'Download CSV',
            icon: 'pi pi-file-excel',
            command: () => exportCSV(false)
        }
    ];

    return (
        <div className="w-full">
            <Toast ref={toast}/>
            <AppCard title="All Agents Report">

                <div
                    className="flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center mb-6 bg-base-100/50 p-4 rounded-xl border border-base-200">
                    <div className="join w-full xl:w-auto">
                        <InputText
                            id="agentId"
                            value={agentId}
                            onChange={(e) => {
                                const numericValue = e.target.value.replace(/\D/g, '');
                                setAgentId(numericValue);
                            }}
                            placeholder="Agent ID"
                            inputMode="numeric"
                            className="input input-bordered join-item h-10 w-full md:w-48"
                        />
                        <Calendar
                            id="fromDate"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.value)}
                            dateFormat="yy-mm-dd"
                            placeholder="From Date"
                            inputClassName="input input-bordered join-item h-10 w-full md:w-36"
                            className="join-item"
                        />
                        <Calendar
                            id="toDate"
                            value={toDate}
                            onChange={(e) => setToDate(e.value)}
                            dateFormat="yy-mm-dd"
                            placeholder="To Date"
                            inputClassName="input input-bordered join-item h-10 w-full md:w-36"
                            className="join-item"
                        />
                        <button
                            className="btn btn-primary join-item text-white font-semibold"
                            onClick={fetchAgentReport}
                            disabled={loading}
                        >
                            Generate
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto ml-auto">
                        <div className="relative w-full sm:w-64">
                            <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
                            <input
                                type="text"
                                className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
                                placeholder="Search..."
                                value={globalFilter}
                                onChange={onGlobalFilter}
                            />
                        </div>
                        <div className="dropdown dropdown-end w-full sm:w-auto">
                            <div tabIndex={0} role="button"
                                 className="btn btn-outline w-full sm:w-auto btn-ghost h-10 min-h-0 shadow-sm rounded-full"
                                 disabled={payments.length === 0}>
                                <i className="pi pi-file-export"></i> Export
                            </div>
                            <ul tabIndex={0} className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                                    <li><a onClick={() => exportToCSV(dt, 'Agent Payments')}><i className="pi pi-file-excel text-success"></i> Download CSV</a></li>
                                    <li><a onClick={() => exportToPDF(dt, 'Agent Payments', 'Agent Payments Report')}><i className="pi pi-file-pdf text-error"></i> Download PDF</a></li>
                                </ul>
                        </div>
                    </div>
                </div>

                <DataTable
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ref={dt}
                    value={payments}
                    loading={loading}
                    globalFilter={globalFilter}
                    exportFilename="agents-report"
                    emptyMessage="No payment records found. Please select a date range and generate report."
                    paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                >
                    <Column field="policyNumber" header="Policy Number" sortable/>
                    <Column field="productName" header="Product Name" sortable/>
                    <Column field="payerName" header="Payer Name" sortable/>
                    <Column field="mobileNumber" header="Mobile Number" sortable/>
                    <Column field="emailAddress" header="Email" sortable/>
                    <Column field="paymentMethod" header="Payment Method" body={paymentMethodBodyTemplate} sortable/>
                    <Column field="amount" header="Amount" body={amountBodyTemplate} sortable/>
                    <Column field="paymentStatus" header="Payment Status" body={paymentStatusBodyTemplate} sortable/>
                    <Column field="paymentReference" header="Payment Ref" sortable/>
                    <Column field="notes" header="Notes"/>
                    <Column field="timeStamp" header="Timestamp"
                            body={(rowData) => dateBodyTemplate(rowData, 'timeStamp')} sortable/>
                </DataTable>
            </AppCard>
        </div>
    );
};

export default AgentReportsTable;
